import { FileText } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { FormSheet } from '@/components/ui/FormSheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import { refusalMessage } from '@/lib/refusals'
import { useCaseInvoices } from '@/features/finance/api/useInvoices'
import {
  useCaseDocuments,
  useRaiseDocument,
} from '@/features/finance/api/useFinance'
import { CaseRateSchedule } from '@/features/cases/components/CaseRateSchedule'
import { DocumentRowActions } from '@/features/finance/components/DocumentRowActions'
import {
  DebitNoteFormSheet,
  type CounterpartyChoice,
} from '@/features/finance/components/FinanceFormSheets'
import { useRaiseInvoiceDraft } from '@/features/finance/api/useRaiseInvoiceDraft'
import { NewDocumentMenu } from '@/features/finance/components/NewDocumentMenu'
import type { LineDraft } from '@/features/finance/lines'
import { documentStatusOf, isSuppressed } from '@/types/domain'
import type {
  CaseDetail,
  CaseParticipant,
  DocumentStatus,
  ExpenseLine,
} from '@/types/domain'

/* The case's INVOICES section: the rate that was agreed, the payments it is
 * collected in, and the paper raised against it.
 *
 * ── Why this grew here rather than beside the Documents tab ────────────────
 *
 * It IS the Documents tab, renamed. The rail key stays `documents` — it is what
 * the URL carries, what `BackToCase` links to and what a colleague has already
 * pasted into a chat — and only the label moved, to "Invoices". A separate
 * "Invoices" section next to a "Documents" one would be two tabs about one
 * piece of paper, and the reader's first act on every visit would be guessing
 * which of the two holds the invoice they came for. There is one place a case's
 * billing is read, and the documents table is the bottom half of it: the
 * schedule says what is still to be asked for, and the table says what has
 * actually been sent.
 *
 * The rate and the schedule live in `CaseRateSchedule`; what follows is the
 * archive half.
 *
 * The paper raised against one case: its invoices and its debit notes, in the
 * place the person arguing about them is already standing.
 *
 * The section is a VIEW of the finance archive, not a second one. It reads the
 * same two endpoints the Money page reads — `/cases/:id/invoices` and
 * `/cases/:id/debit-notes` — through the same hooks and so out of the same cache
 * entries, renders the same status vocabulary, and raises through the same sheet
 * and the same mutation. So a debit note opened here appears on the Money page
 * without a refresh, and neither surface can grow a rule the other does not
 * have. What the case adds is the filter and the prefill: the case, its client
 * and its participants are facts this page already knows, and making somebody
 * retype them onto the document is how an invoice ends up filed against the
 * wrong case.
 *
 * (Until issue #27 the same paragraph described a FIXTURE: both screens called
 * `/api/finance/documents`, which is `internal/showcase` — memory in the server
 * process, where a document raised here was stored nowhere and could not be
 * found again. The argument survives the move unchanged; only the premise
 * became true.)
 *
 * Money vs Documents is the split between the deal and its paper. Money answers
 * "what did this case cost and what is still owed"; this answers "what have we
 * actually sent the client, and has it been paid". They are next to each other
 * on the rail because that is the order the questions get asked in.
 *
 * ── The three stages, and where the actions live now ───────────────────────
 *
 * A document here is DRAFT, then RENDERED, then ISSUED, and the three are three
 * separate presses because they are three separate decisions (00-decisions §1).
 * Generating is not issuing (UX-DR-15): two calls, one confirmation on the
 * irreversible one, and no "issue on generate" shortcut — which is exactly what
 * `document.Handlers.Issue`'s comment refuses, since a flag is something a
 * client sets by accident.
 *
 * Those presses used to live in a 400px panel beside this table, against
 * whichever row was selected, and the argument for the panel was that its
 * buttons named ONE document — "a Generate button repeated down a table is a
 * button somebody presses on whichever line their finger landed on". The
 * concern was right and the remedy was expensive: the panel took a third of the
 * card away from a five-column list of money, and it also held a hand-drawn
 * approximation of the document, which is a document nobody has checked.
 *
 * Both halves moved, and the concern is answered better than the panel answered
 * it. The actions are in `DocumentRowActions`, on the row they act on — an icon
 * inside a row names its document more plainly than a panel floating beside a
 * selection — and the two irreversible ones do not act on the press at all:
 * they open a dialogue that names the consequence in words. The preview is now
 * the SERVER's own page, composed by the same template the client's PDF comes
 * off (`InvoicePreviewSheet`), so the screen can no longer disagree with the
 * paper.
 */

/** The pill's tone per derived status.
 *
 *  Four rows for four states, and there is no `void`: AD-24 makes Draft →
 *  Issued one-way and nothing in this system moves a document back out of
 *  Issued — a correction is a NEW document, which is how the paper world does
 *  it. A fifth row here would be a tone waiting for a state nothing can produce,
 *  the same defect the money section had when it offered "money is not shown for
 *  your role" to a founder holding every role.
 *
 *  Tones only, never translated strings: this is a module constant evaluated
 *  once at import, and a word baked in here would freeze in whichever language
 *  was active at startup. */
const DOC_STATUS_TONES: Record<
  DocumentStatus,
  'neutral' | 'brand' | 'positive' | 'attention' | 'negative'
> = {
  draft: 'neutral',
  issued: 'brand',
  'part-paid': 'attention',
  paid: 'positive',
}

/** A case participant as the WIRE serves it.
 *
 *  `casefile.ParticipantDTO` carries `directoryId` — the counterparty this party
 *  actually IS, served in the clear even to a caller whose role suppresses the
 *  name, because an opaque uuid names nothing by itself — and `CaseParticipant`
 *  in `types/domain` does not model it yet. It is narrowed here rather than read
 *  off `participant.id`, and the difference is not cosmetic: `id` is the
 *  case_participants ROW, while a debit note's create resolves
 *  `counterpartyKind: 'participant'` through `directory.GetParticipant` (AD-26).
 *  Sending the row id would name nothing in that directory, and every
 *  participant offered on this page would come back as a 422 on the one field
 *  the user cannot fix.
 *
 *  Optional, and the absence is meaningful: a party who is a name off a
 *  transport document has no directory row at all and cannot be a debit note's
 *  recipient — there is no company for the server to freeze a printed name and
 *  address from. Those are filtered out below rather than offered and refused.
 */
interface LinkedParticipant extends CaseParticipant {
  directoryId?: string
}

/** Picks case costs to bill, before the DEBIT NOTE sheet opens.
 *
 *  ── It used to be the first screen of every document, and that was the bug ──
 *
 *  This sheet was the front door of "New document": pressing it asked, before
 *  anything else, which of the case's recorded COSTS to bill on — and ticking
 *  one silently made the document a debit note, because charges can only ride
 *  on one. So the ordinary act of invoicing the client began with a question
 *  about the company's own cost side, and answering it turned a demand on the
 *  client into a demand on a counterparty. The picker is not the thing that was
 *  wrong; its POSITION was. It now opens inside the debit-note branch of the
 *  fork, where a case's costs genuinely are where the lines come from.
 *
 *  The other half of "where do the lines come from". Typing a debit note from
 *  scratch is the general answer; this is the common one, because the charges
 *  being billed on are usually costs somebody already recorded against the case
 *  — the detention, the inspection, the storage. Retyping them from the Money
 *  tab is both slower and a chance to transpose a figure.
 *
 *  It seeds and then gets out of the way: what it produces is an ordinary set
 *  of draft lines, editable like any other. The description and amount are
 *  carried across; the quantity is 1, because an expense line records a total
 *  rather than a rate and a count, and inventing a breakdown here would be
 *  putting a quantity on the paper that nobody stated.
 */
function ExpensePickerSheet({
  open,
  expenses,
  onClose,
  onConfirm,
}: {
  open: boolean
  expenses: ExpenseLine[]
  onClose: () => void
  onConfirm: (lines: LineDraft[]) => void
}) {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<Set<string>>(new Set())

  // Re-seeded per open like the form sheets, so a cancelled pick does not carry
  // its ticks into the next one.
  const [seededFor, setSeededFor] = useState(open)
  if (open !== seededFor) {
    setSeededFor(open)
    if (open) setChosen(new Set())
  }

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const selected = expenses.filter((expense) => chosen.has(expense.id))
  const total = selected.reduce(
    (sum, expense) => sum + Number(expense.actual?.amount ?? 0),
    0,
  )

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.documents.pick.title')}
      submitLabel={t('cases.documents.pick.confirm')}
      // Skipping is a first-class outcome, not a cancel: a debit note for
      // something never recorded as a cost is ordinary.
      disabled={false}
      onSubmit={() =>
        onConfirm(
          selected.map((expense, index) => ({
            key: `expense-${expense.id}-${index}`,
            description: expense.description,
            note: '',
            unitPrice: expense.actual?.amount ?? '0',
            quantity: '1',
          })),
        )
      }
    >
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('cases.documents.pick.description')}
      </p>

      <ul className="flex flex-col gap-1.5">
        {expenses.map((expense) => {
          const ticked = chosen.has(expense.id)
          return (
            <li key={expense.id}>
              <label
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-md)]',
                  'border px-3 py-2.5 transition-colors',
                  ticked
                    ? 'border-brand/40 bg-brand-soft'
                    : 'border-line-hair hover:bg-surface-hover',
                )}
              >
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={() => toggle(expense.id)}
                  className="accent-brand size-4 shrink-0 cursor-pointer"
                />
                <span className="text-text-heading min-w-0 flex-1 text-[13px]">
                  {expense.description}
                </span>
                <span className="asa-num text-text-body shrink-0 text-[12.5px]">
                  {formatNumber(expense.actual?.amount, 2)}
                </span>
              </label>
            </li>
          )
        })}
      </ul>

      <div className="border-line-hair flex items-baseline justify-between border-t pt-3">
        <span className="asa-eyebrow">{t('cases.documents.pick.selected')}</span>
        <span className="asa-num text-text-heading text-[14px] font-semibold">
          {t('cases.documents.pick.count', { count: selected.length })}
          <span className="text-text-muted mx-1.5" aria-hidden>
            ·
          </span>
          {formatNumber(String(total), 2)}
        </span>
      </div>
    </FormSheet>
  )
}
export function CaseDocuments({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const [seededLines, setSeededLines] = useState<LineDraft[]>([])
  // The case ID, not its number: the real reads are nested under the id, which
  // is also the id the row scope is checked against. The fixture keyed on the
  // number because it had nothing better.
  const { data, isPending, isError, error, refetch } = useCaseDocuments(record.id)
  /* The invoices ON THEIR OWN as well as merged into the table above, because
     the schedule needs the one field the merged view drops: `trancheId`, which
     is what says which instalment a demand replaced. Not a second request —
     `useCaseDocuments` calls this same hook, so both readers come out of one
     cache entry and cannot disagree. */
  const invoices = useCaseInvoices(record.id)
  const raise = useRaiseDocument()
  /* One raise for both entry points on this section — the fork's "Client
     invoice" and every "Raise invoice" button on the schedule — so a refusal
     lands once and a press in flight turns both off. */
  const raiseInvoice = useRaiseInvoiceDraft(record.id, record.money?.rate.currency)

  /* The case's recorded costs, offered as charges to bill on. Only lines with a
     figure are offerable: a planned-but-unrecorded cost is an estimate, and
     billing a client from an estimate is how a debit note gets disputed.

     Absent entirely for a role with no commercial access — `money` is undefined
     then (AD-6), and the picker simply is not offered rather than appearing
     empty. */
  const billable = record.money?.expenses.filter((expense) => expense.actual) ?? []

  /* Who this case's debit notes may be addressed to (AD-26): its client, and
     the participants that are directory rows. Composed HERE and passed down,
     because the sheet has no business fetching a directory — a picker fed from
     one would offer every company in the system, which is how a demand for money
     gets addressed to a party with nothing to do with the shipment.
     `CounterpartyChoice` makes the same argument from the sheet's side.

     A participant whose NAME is suppressed is left out. The reader cannot see
     who it is, so offering it would be offering to address a demand to a company
     they are not allowed to know exists — and the server refuses that party
     anyway (`directory.GetParticipant` answers not-found for an agent a Sales
     caller asks about), so the option could only ever fail. */
  const counterparties = useMemo<CounterpartyChoice[]>(() => {
    const parties: LinkedParticipant[] = record.participants
    const client: CounterpartyChoice[] =
      record.clientId === undefined
        ? []
        : [{ id: record.clientId, name: record.clientName, kind: 'client' }]

    return [
      ...client,
      ...parties.flatMap<CounterpartyChoice>((party) =>
        party.directoryId === undefined || isSuppressed(party.name)
          ? []
          : [{ id: party.directoryId, name: party.name, kind: 'participant' }],
      ),
    ]
  }, [record.clientId, record.clientName, record.participants])

  /* The debit-note branch of the fork, and the only branch that opens a form
     at all: the client invoice posts an empty header and goes to its page (see
     `NewDocumentMenu`). */
  const startDebitNote = () => {
    setSeededLines([])
    // The picker is a step, not a gate: it is offered only when there is
    // something to pick, and it can always be skipped into a blank sheet.
    if (billable.length > 0) setPicking(true)
    else setOpen(true)
  }

  const sheet = (
    <>
      <ExpensePickerSheet
        open={picking}
        expenses={billable}
        onClose={() => setPicking(false)}
        onConfirm={(lines) => {
          setSeededLines(lines)
          setPicking(false)
          setOpen(true)
        }}
      />

      <DebitNoteFormSheet
        open={open}
        onClose={() => setOpen(false)}
        /* The prefill, and the whole reason to raise a document from the case
           rather than from Money: the case is a fact this page holds, and both
           creates are nested under it. Nobody retypes a case number here,
           because retyping one is how an invoice ends up filed against the wrong
           shipment. */
        caseId={record.id}
        counterparties={counterparties}
        defaults={{ lines: seededLines }}
        pending={raise.isPending}
        /* Our sentence, never the server's. `raise.error.message` is the problem
           document's `detail` — written server-side in English for a log — and
           putting it on this sheet handed a Russian operator a second language
           at the one moment they are least able to cope with it (AD-22, AD-27).
           `refusalMessage` also names the fields of an incomplete issuing entity
           (FR-64), which the generic mapping cannot: `issuingEntity` is not a
           control on this form, so "the highlighted fields could not be
           accepted" would highlight nothing. */
        submitError={raise.isError ? refusalMessage(raise.error, t) : undefined}
        /* Closed from onSuccess, so a refusal leaves the sheet open holding the
           draft rather than closing over a save that never happened.

           The new note is no longer "selected" afterwards, because there is no
           selection any more: it appears in the table with its own actions on
           its own row. A debit note has no page to navigate to either — unlike
           an invoice, which is why the fork's other branch goes somewhere and
           this one does not. */
        onSubmit={(payload) =>
          raise.mutate(payload, { onSuccess: () => setOpen(false) })
        }
      />
    </>
  )

  /* The archive's three states as a NODE rather than three early returns, and
     that is what the restructure bought: the rate and the schedule above are
     read off the CASE record, which has already arrived, and a document list
     still in flight — or refused — must not take them off the screen with it.
     The old section returned a bare skeleton for the whole tab.

     Both reads or neither on the list itself: the composite is pending while
     EITHER is in flight, because a section that showed the invoices while the
     debit notes were still loading would announce three documents and then
     quietly become five. The `data === undefined` half is the same statement in
     the shape TypeScript can narrow — the composite is a plain object rather
     than a discriminated `useQuery` result, so its `isPending` narrows nothing
     by itself. */
  const archive = isError ? (
    <div className="px-[18px] pb-4">
      <ErrorState error={error} onRetry={() => void refetch()} />
    </div>
  ) : isPending || data === undefined ? (
    <div className="px-[18px] pb-4">
      <Skeleton className="h-40 rounded-[var(--radius-DEFAULT)]" />
    </div>
  ) : data.length === 0 ? (
    /* An empty state says what the surface is FOR. It carries no action of its
       own any more: the fork sits in the card header a few pixels above, and a
       second control with the same accessible name is two answers to one
       question — for a screen reader, and for a test. */
    <EmptyState
      icon={FileText}
      title={t('cases.documents.emptyTitle')}
      description={t('cases.documents.emptyDescription')}
    />
  ) : (
    /* ONE table, the full width of the card.
     *
     * It used to be a table beside a 400px panel that held the selected
     * document's actions and a hand-drawn sketch of its layout. The panel took
     * roughly a third of the card from a five-column list of money — the
     * counterparty wrapped onto two lines and the outstanding column fell off
     * the right edge — and what it bought was an approximation of a document
     * nobody had checked, plus three buttons naming one row.
     *
     * Both jobs moved. The actions live on the row they act on, which names
     * the document more plainly than a panel beside a selection ever did; the
     * preview is now the SERVER's page, composed by the same template the
     * client's PDF comes off, opened in a sheet from the row. There is
     * therefore nothing left to select, and `selectedId` went with the panel.
     */
    <div className="asa-scroll min-w-0 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 px-[18px]">
                  {t('common.fields.number')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('finance.balances.counterparty')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('common.fields.status')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('common.fields.total')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('finance.documents.outstanding')}
                </th>
                {/* The actions column carries no heading. "Actions" over a row
                    of icons that each name themselves is a word that tells
                    nobody anything, and the empty header keeps the column
                    widths honest. */}
                <th scope="col" className="h-9 w-px px-[18px]">
                  <span className="sr-only">{t('common.actions.label')}</span>
                </th>
              </tr>
            </thead>
            {/* There is NO Due column, and its absence is a correction rather
                than a simplification. The fixture served a `dueOn` and this
                table washed a row red against it; neither `InvoiceSummary` nor
                `DebitNote` carries a due date at all, and the nearest real fact
                — the issuing entity's payment terms — is a phrase PRINTED on the
                paper rather than a date this side may compute one from. A column
                derived from it would put a deadline on screen that no record
                holds and nobody agreed to, which is worse than no column. The
                `overdue` derivation went with it. */}
            <tbody>
              {(data ?? []).map((document) => {
                const status = documentStatusOf(document)
                return (
                  <tr
                    key={document.id}
                    className="border-line-hair hover:bg-surface-hover h-[38px] border-b transition-colors last:border-b-0"
                  >
                    <td className="px-[18px]">
                      {/* The number, and on an invoice it is the way in: a link
                          rather than a button, because it goes to a page and a
                          reader is entitled to open it in a new tab. A debit
                          note has no page of its own, so it renders as plain
                          text rather than as a link that goes nowhere. */}
                      <span className="flex items-center gap-2">
                        {/* Both numbering styles coexist by design: the Ashgabat
                            AB-2026-0184 form and the Yiwu ASADN26070001 form
                            resolve from the issuing entity (FR-68). A draft has
                            NO number — minting belongs to the issuing
                            transaction (AD-12) — so the word "Draft" stands
                            where the number will be, as a label and never as a
                            value. The fixture printed the literal string "DRAFT"
                            as the number, which is the thing this is not. */}
                        {document.kind === 'invoice' ? (
                          <Link
                            to={`/cases/${document.caseId}/invoices/${document.id}`}
                            className="text-brand font-mono text-[12.5px] font-semibold hover:underline"
                          >
                            {document.number ?? t('finance.documents.preview.draftBadge')}
                          </Link>
                        ) : (
                          <span className="text-brand font-mono text-[12.5px] font-semibold">
                            {document.number ?? t('finance.documents.preview.draftBadge')}
                          </span>
                        )}
                        <Tag>{t(`documentKind.${document.kind}`)}</Tag>
                      </span>
                    </td>
                    <td className="text-text-body px-3 text-[13px]">
                      {/* AD-6: a debit note's recipient is withheld from a caller
                          without agent-identity visibility, and the marker is
                          rendered rather than a blank — a blank reads as a field
                          nobody filled in, which is somebody's cue to go "fix"
                          it. The money on the row stays visible either way; only
                          the identity is suppressed, which is the whole of
                          FR-29. */}
                      <SuppressedValue value={document.counterpartyName} />
                    </td>
                    <td className="px-3">
                      {/* DERIVED, by the one helper both Documents surfaces
                          call. Two copies of this branch would let one screen
                          call a document part-paid while the other still called
                          it issued, which is the sort of disagreement a person
                          resolves by distrusting both. */}
                      <StatusPill tone={DOC_STATUS_TONES[status]}>
                        {t(`docStatus.${status}`)}
                      </StatusPill>
                    </td>
                    <td className="asa-num text-text-body px-3 text-right text-[13px]">
                      {formatNumber(document.total.amount, 2)}
                    </td>
                    <td className="asa-num text-text-heading px-3 text-right text-[13px]">
                      {formatNumber(document.outstanding.amount, 2)}
                    </td>
                    <td className="px-[18px]">
                      {/* Keyed by the record, so moving between documents never
                          leaves one row's refusal standing under another's
                          icons. */}
                      <DocumentRowActions
                        key={document.id}
                        record={document}
                        onOpen={
                          document.kind === 'invoice'
                            ? () =>
                                void navigate(
                                  `/cases/${document.caseId}/invoices/${document.id}`,
                                )
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
    </div>
  )

  return (
    /* Two cards, one section, read top to bottom: what the client agreed to pay
       and what is left of it, then what has actually been sent. */
    <div className="flex min-w-0 flex-col gap-4">
      <CaseRateSchedule
        record={record}
        invoices={invoices.data ?? []}
        raiseInvoice={raiseInvoice}
      />

      <Card>
        <CardHeader
          action={
            <NewDocumentMenu
              caseId={record.id}
              currency={record.money?.rate.currency}
              onDebitNote={startDebitNote}
              raiseInvoice={raiseInvoice}
            />
          }
        >
          <CardTitle>{t('cases.documents.title')}</CardTitle>
          <CardSubtitle>{t('cases.documents.subtitle')}</CardSubtitle>
        </CardHeader>

        {archive}
      </Card>

      {sheet}
    </div>
  )
}
